/**
 * @fileoverview 動画詳細ページ・特定ID（自社サービス）パターン用 Main パーシャル
 * @description ロック中（未contact）は document と同型の2カラムフォーム（LayoutSolutionContentsShowPage）、
 *              解放済み（contact済み）は1カラムのプレイヤー（SingleColumnLayout）を表示する。
 *              state は親(Main)が保持。フォーム入力イベントは v-bind="$attrs" で親へ素通しする
 *              （子が emit したイベントもそのまま親へ伝わる。中間層での再 emit は不要）。
 * @see https://ja.vuejs.org/guide/components/attrs.html
 */
<script lang="ts">
import { defineComponent, PropType } from 'vue'
import LayoutSolutionContentsShowPage from '@/components/solutions/LayoutSolutionContentsShowPage.vue'
import SingleColumnLayout from './SingleColumnLayout.vue'

interface State {
  form: Form,
  input: {
    userInfo: {
      lastName: InputText,
      firstName: InputText,
      lastNameKana: InputText,
      firstNameKana: InputText,
      companyName: InputText,
      industry: InputText,
      scale: InputText,
      listingStatus: InputText,
      profession: InputText,
      professionDomain: InputMultiSelect,
      positionLevel: InputText,
      departmentAndPosition: InputText,
      workMailAddress: InputText,
      phoneNumber: InputText,
      corporateZipcode: InputText,
      corporateAddress1: InputText,
      corporateAddress2: InputText,
      corporateNumber: InputText,
      corporateEmployeeNum: InputNumber,
    },

    optionalNewsletterSettings: {
      mailStopFlag: InputBoolean,
      mailNewsStopFlag: InputBoolean,
      mailExpoStopFlag: InputBoolean,
      mailAiStopFlag: InputBoolean,
    },

    customFields: Inputs,
    hearingFields: Inputs,

    isFullFilled: boolean,
  },
  video: SolutionContentItem,

  /**
   * Custom Field のサーバからの情報受取用
   * ※ Custom Field の入力値格納用は State の input オブジェクトに別途用意
   */
  customFieldConfigItems?: CustomFieldConfig[],

  /**
   * ヒアリング項目のサーバからの情報受取用
   * ※ ヒアリング項目の入力値格納用は State の input オブジェクトに別途用意
   */
  hearingFieldConfig?: HearingFieldConfig,

  meta: {
    corporateBrandColor: {
      primary: string,
      secondary: string,
    },
  },
}

export default defineComponent({
  name: 'VideoMainB',

  components: {
    LayoutSolutionContentsShowPage,
    SingleColumnLayout,
  },

  // フォーム入力イベントは $attrs で親へ素通しするため、自動継承は無効化して手動 bind する
  inheritAttrs: false,

  props: {
    state: {
      type: Object as PropType<State>,
      required: true,
    },

    /**
     * コンテンツ種別の呼称（「動画」/「音声」）
     */
    mediaTypeLabel: {
      type: String,
      required: true,
    },
  },

  setup() {
    return {}
  },
})
</script>

<template>
  <!-- ロック中（未contact）: 2カラムフォーム -->
  <LayoutSolutionContentsShowPage
    v-if="state.video.isShowBlock"
    v-bind="$attrs"
    :contents="state.video"
    :form="state.form"
    :input-user-info="state.input.userInfo"
    :input-optional-newsletter-info="state.input.optionalNewsletterSettings"
    :input-custom-fields="state.input.customFields"
    :custom-field-config-items="state.customFieldConfigItems"
    :input-hearing-fields="state.input.hearingFields"
    :hearing-field-config="state.hearingFieldConfig"
    :is-full-filled="state.input.isFullFilled"
    :corporate-brand-color="state.meta.corporateBrandColor"
    :has-heading="true"
    :submit-button-label="`${mediaTypeLabel}を視聴する`">
    <!-- 動画サムネイル（非同期取得）を画像領域に注入 -->
    <template #thumbImage>
      <img
        v-if="state.video.thumbImageUrl"
        class="img"
        :src="state.video.thumbImageUrl"
        fetchpriority="high"
        alt=""
        data-is-liquid="true">
    </template>

    <template #step1HeadingDescription>
      こちらは<strong class="strong">会員様限定</strong>の貴重な{{ mediaTypeLabel }}です。<br>無料会員登録で{{ mediaTypeLabel }}をご視聴いただけます。
    </template>
    <template #step1SubmitLabel>
      登録して{{ mediaTypeLabel }}を視聴する(無料)
    </template>
    <template #step2Heading>
      {{ mediaTypeLabel }}を視聴するには次の項目をご入力ください
    </template>
    <template #step3Heading>
      {{ mediaTypeLabel }}を視聴するには次の項目をご入力ください
    </template>
  </LayoutSolutionContentsShowPage>

  <!-- 解放済み（contact済み）: 1カラムのプレイヤー -->
  <SingleColumnLayout
    v-else
    :video="state.video"
    :corporate-brand-color="state.meta.corporateBrandColor" />
</template>
